/* One remediation plan: what it would change, what it cannot, and why it may not proceed.
 *
 * **Every cell in the Proposals table is mechanism, never a value** — which transformation, which
 * source, which citation. Nothing on this screen shows what is in a row, and there is no field in
 * the payload a customer value would fit in.
 *
 * **"Unresolved" is the number a single total hides.** 400,000 rows flagged with 12 changed is not
 * a fix, and a screen showing only "would change" would report it as one.
 *
 * **The assessment is a button, not something a page load does.** `POST /execution-assessment`
 * dials a connection to the customer's production database and counts rows in it — a rung-2 read
 * somebody has to have decided to make. That is the one deliberate deviation from the reference,
 * which draws the section as always-present: rendering it on mount would scan a bank's tables on
 * every page view, and the platform gates the route on `execute` rather than `read` for exactly
 * that reason.
 *
 * **There is no Execute control here or anywhere.** No write adapter exists, so a button could only
 * fail on click. Its absence is the honest answer, and the queue says so once at the top rather
 * than repeating it per plan.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import type {
  ExecutionAssessment,
  Plan,
  Proposal,
} from '@/shared/api/generated/remediation';
import { downloadFile } from '@/shared/api/http';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { TextField } from '@/shared/ui/Field';
import { SCREEN_PATHS } from '@/shared/routes';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useDataset, useSchemaHistory } from '@/features/catalog-browser';

import { useAssessExecution } from '../api/execution';
import {
  artifactUrl,
  usePlan,
  usePlanAction,
  usePlanArtifacts,
  useScriptPreview,
} from '../api/plans';
import {
  asInstant,
  blockedFor,
  factsOf,
  flaggedOnly,
  hasArtifact,
  isApplicable,
  lifecycleOf,
  decisionNotice,
  offeredFor,
  proposalRows,
  relationOf,
  modeConsequenceKey,
  rowsReading,
  signingLines,
  stateLabelKey,
  stateToneOf,
  tierLabelKey,
  toneOf,
  unfixableCount,
} from '../model/plans';
import type { Fact } from '../model/plans';

/** A refusal's tone. `no_write_adapter` is `notice`, not a failure: nothing is wrong with the plan.
 *
 * `refusal` arrives as `string` rather than a union — the contract's enum lists `null` among its
 * members, so the generator widens it — which means a server one version ahead sending an eighth
 * reason is genuinely reachable. It falls back to `warning` rather than to the permitted tone,
 * which is the mistake §9 records `verdictTone`'s `default:` making in `core.ai.evals`.
 */
const REFUSAL_TONE: Readonly<Record<string, 'warning' | 'notice' | 'error'>> = {
  not_approved: 'warning',
  wrong_state: 'notice',
  not_simulated: 'warning',
  unsafe_tier: 'error',
  not_reversible: 'error',
  out_of_region: 'error',
  no_write_adapter: 'notice',
};

function ProposalsTable({
  plan,
  assessment,
}: {
  readonly plan: Plan;
  readonly assessment: ExecutionAssessment | null;
}): ReactElement {
  const { t } = useTranslation();
  /* Keyed on the proposal id, so a preview lands against its own row rather than against whichever
     row happened to share an index. The assessment's preview list is only the applicable ones. */
  const previews = new Map(
    (assessment?.previews ?? []).map((preview) => [preview.proposal_id, preview]),
  );

  return (
    <div className="lq-table-scroll lq-table-scroll--framed lq-rm__proposals">
      <table className="lq-table lq-table--ledger">
        <caption className="lq-visually-hidden">
          {t('remediation.proposalsCaption', { plan: plan.id })}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t('remediation.column.target')}</th>
            <th scope="col">{t('remediation.column.tier')}</th>
            <th scope="col">{t('remediation.column.role')}</th>
            <th scope="col">{t('remediation.column.wouldDo')}</th>
            <th scope="col" className="lq-num">
              {t('remediation.column.wouldChange')}
            </th>
            <th scope="col" className="lq-num">
              {t('remediation.column.unresolved')}
            </th>
          </tr>
        </thead>
        <tbody>
          {plan.proposals.map((proposal) => {
            const preview = previews.get(proposal.id);
            const rows = proposalRows(proposal);
            return (
              <tr
                key={proposal.id}
                className={isApplicable(proposal) ? undefined : 'lq-rm__flaggedRow'}
              >
                <td>
                  <span className="lq-mono">{proposal.column}</span>
                  <div className="lq-cell__secondary lq-mono">
                    {proposal.qualified_name}
                  </div>
                </td>
                <td className="lq-nowrap">
                  <span
                    className={`lq-tag lq-tag--rmtier-${toneOf(proposal)}`}
                    title={t(tierLabelKey(proposal.tier))}
                  >
                    <span className="lq-tag__dot" aria-hidden="true" />
                    {t('remediation.tierBadge', {
                      tier: proposal.tier,
                      meaning: t(tierLabelKey(proposal.tier)),
                    })}
                  </span>
                </td>
                <td className="lq-nowrap">
                  {t(`remediation.role.${proposal.column_role}`)}
                  <div className="lq-cell__secondary">
                    {t(`remediation.roleNote.${proposal.column_role}`)}
                  </div>
                </td>
                <td>
                  {/* Mechanism only. A Tier D row has no action at all, which is why it says so
                      rather than showing a blank that reads as a rendering fault. */}
                  {proposal.action === null ? (
                    <span className="lq-rm__noAction">
                      {t('remediation.noDerivation')}
                    </span>
                  ) : (
                    <>
                      <span className="lq-mono">
                        {t(`remediation.operation.${proposal.action.operation}`)}
                      </span>
                      <div className="lq-cell__secondary lq-rm__derivation">
                        {t(`remediation.source.${proposal.action.value_source}`)}
                        {/* `?? null` rather than `=== null`: the field is optional on the wire,
                            so an absent one is `undefined` and a null check alone would put the
                            word "undefined" on screen beside a customer's column name. */}
                        {(proposal.action.reference ?? null) === null
                          ? ''
                          : ` · ${String(proposal.action.reference)}`}
                      </div>
                    </>
                  )}
                  <div className="lq-rm__rationale">{proposal.rationale}</div>
                </td>
                <td className="lq-num lq-nowrap">
                  {!isApplicable(proposal) ? (
                    <span className="lq-muted">{t('remediation.never')}</span>
                  ) : rows.state === 'unsimulated' ? (
                    <span className="lq-muted">{t('remediation.notSimulated')}</span>
                  ) : (
                    rows.rows.toLocaleString()
                  )}
                </td>
                <td className="lq-num lq-nowrap" title={t('remediation.unresolvedHint')}>
                  {/* Only an assessment measures this. Before one has run the honest answer is that
                      nobody has asked — a zero here would say the fix covers every flagged row. */}
                  {preview === undefined ? (
                    <span className="lq-muted">{t('remediation.notAssessed')}</span>
                  ) : (
                    <span
                      className={
                        preview.rows_unresolved > 0 ? 'lq-rm__unresolved' : undefined
                      }
                    >
                      {preview.rows_unresolved.toLocaleString()}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function PlanDetail(): ReactElement {
  const { t } = useTranslation();
  const { planId = '' } = useParams();
  const plan = usePlan(planId);
  const assess = useAssessExecution();
  /* The dataset's version history, read only to turn this plan's `dataset_version_id` into an
     ordinal. Enabled once the plan has loaded, because the dataset id comes off it — and a failed
     read is not a failure of this screen: `factsOf` falls back to the id. */
  const versions = useSchemaHistory(plan.data?.dataset_id ?? '');

  /* The relation once the plan has loaded, the id until then. A breadcrumb that changed from a
     name to an id on a failed read would read as navigation rather than as a failure. */
  const heading = (plan.isSuccess ? relationOf(plan.data) : null) ?? planId;
  const crumb = (
    <div className="lq-cp__crumb">
      <Link to={SCREEN_PATHS.remediation}>{t('remediation.title')}</Link>
      {' / '}
      <span className="lq-mono">{heading}</span>
    </div>
  );

  if (plan.isPending) {
    return (
      <div className="lq-rm lq-refscreen">
        {crumb}
        <SkeletonRows rows={4} columns={6} />
      </div>
    );
  }
  if (plan.isError) {
    return (
      <div className="lq-rm lq-refscreen">
        {crumb}
        <EmptyState
          title={t('remediation.notFound.title')}
          guidance={plan.error.message}
        />
      </div>
    );
  }

  const found = plan.data;
  const relation = relationOf(found);
  const tone = stateToneOf(found.state);
  const flagged = flaggedOnly(found);
  const rows = rowsReading(found);
  /* The mutation is a single-slot observer, so `variables` must be checked or a stale assessment
     from another plan would render against this one. */
  const assessment = assess.isSuccess && assess.variables === planId ? assess.data : null;

  return (
    <div className="lq-rm lq-refscreen">
      {crumb}

      <div className="lq-cp__detailHead">
        <div className="lq-cp__detailTitle">
          {/* **The table, not the plan's UUID.** A plan is read by somebody deciding whether to
              change a customer's data, and the first thing they need is which table. The id is
              still on the page — the line below, the breadcrumb and the Plan panel — because it is
              what a support conversation and every API call are keyed on. */}
          <h1 className="lq-cp__h1 lq-mono">
            {relation ?? t('remediation.unnamedRelation')}
          </h1>
          <div className="lq-cp__planId lq-mono" title={t('remediation.planIdHint')}>
            {found.id}
          </div>
          <div className="lq-cp__detailDesc">
            {t('remediation.subline', {
              correctable: found.applicable_count,
              flagged: unfixableCount(found),
              mode: t(`remediation.mode.${found.mode}`).toLowerCase(),
            })}
          </div>
        </div>
        <span className={`lq-tag lq-tag--rm-${tone} lq-rm__stateChip`}>
          <span className="lq-tag__dot" aria-hidden="true" />
          {t(stateLabelKey(found.state))}
        </span>
      </div>

      <div className={`lq-band lq-band--rm-${tone} lq-rm__hint`}>
        {t(`remediation.stateHint.${found.state}`)}
      </div>

      {/* **What the signature unlocks, before the field that asks for one.** The approver's whole
          role is to deliberately look, and until this panel the most readable form of the plan was
          the emitted SQL — which does not exist yet, because emission is what the signature
          unlocks. Rendered only while a decision is open: afterwards it would restate the
          proposals table as history. */}
      {found.state === 'awaiting_approval' && <SigningBrief plan={found} />}

      <PlanActions plan={found} />

      <h2 className="lq-sect__title lq-rm__section">{t('remediation.ifExecuted')}</h2>

      {assessment === null ? (
        <div className="lq-cp__none">
          <div className="lq-cp__noneTitle">{t('remediation.assess.title')}</div>
          <div className="lq-cp__noneBody">{t('remediation.assess.body')}</div>
          <div className="lq-rm__assessAction">
            <Button
              tone="secondary"
              busy={assess.isPending}
              disabled={assess.isPending}
              onClick={() => {
                assess.mutate(planId);
              }}
            >
              {t('remediation.assess.run')}
            </Button>
          </div>
          {assess.isError && assess.variables === planId && (
            <Alert tone="error" title={t('remediation.assess.failed')}>
              {assess.error.message}
            </Alert>
          )}
        </div>
      ) : (
        <>
          <Alert
            tone={
              assessment.refusal === null
                ? 'notice'
                : (REFUSAL_TONE[assessment.refusal] ?? 'warning')
            }
            title={
              assessment.refusal === null
                ? t('remediation.wouldProceed')
                : t(`execution.refusal.${assessment.refusal}`)
            }
          >
            {/* The platform's own `detail`, verbatim. Restating it here would be a second place
                deciding what a refusal means, and the two would drift. */}
            {assessment.detail}
            {assessment.terminal ? ` ${t('remediation.terminalNote')}` : ''}
          </Alert>

          <div className="lq-rm__stats">
            <div className="lq-rm__stat">
              <div className="lq-rm__statKey">{t('remediation.stat.rows')}</div>
              <div className="lq-rm__statValue">
                {assessment.rows_to_change.toLocaleString()}
              </div>
              <div className="lq-rm__statNote">{t('remediation.stat.rowsNote')}</div>
            </div>
            <div className="lq-rm__stat">
              <div className="lq-rm__statKey">{t('remediation.stat.flagged')}</div>
              <div className="lq-rm__statValue">
                {assessment.flagged_proposals.length.toLocaleString()}
              </div>
              <div className="lq-rm__statNote">{t('remediation.stat.flaggedNote')}</div>
            </div>
            <div className="lq-rm__stat">
              <div className="lq-rm__statKey">{t('remediation.stat.rollback')}</div>
              <div
                className={
                  assessment.rollback_strategy === 'none_available'
                    ? 'lq-rm__statValue lq-rm__statValue--sm lq-rm__unresolved'
                    : 'lq-rm__statValue lq-rm__statValue--sm'
                }
              >
                {t(`execution.rollbackStrategy.${assessment.rollback_strategy}`)}
              </div>
              <div className="lq-rm__statNote">{t('remediation.stat.rollbackNote')}</div>
            </div>
          </div>
          <p className="lq-note">
            {t('remediation.assessedAt', { when: asInstant(assessment.assessed_at) })}
          </p>
        </>
      )}

      <h2 className="lq-sect__title lq-rm__section">{t('remediation.script.title')}</h2>
      <ScriptSection plan={found} />

      <h2 className="lq-sect__title lq-rm__section">{t('remediation.proposals')}</h2>
      <ProposalsTable plan={found} assessment={assessment} />
      <p className="lq-note">{t('remediation.proposalsNote')}</p>

      {flagged.length > 0 && (
        <div className="lq-rm__flaggedPanel">
          <div className="lq-rm__panelTitle">
            {t('remediation.flaggedPanel', { count: flagged.length })}
          </div>
          {flagged.map((proposal: Proposal) => (
            <div key={proposal.id} className="lq-rm__flaggedEntry">
              <div className="lq-rm__flaggedTarget lq-mono">
                {proposal.qualified_name}.{proposal.column}
              </div>
              <div className="lq-rm__flaggedWhy">{proposal.rationale}</div>
            </div>
          ))}
          <p className="lq-note">{t('remediation.flaggedNote')}</p>
        </div>
      )}

      <div className="lq-rm__pair">
        <div className="lq-rm__panel">
          <div className="lq-rm__panelTitle">{t('remediation.planPanel')}</div>
          {factsOf(found, {
            /* Resolved from the catalogue rather than carried on the plan, because the plan stores
               the version's id and an ordinal is what a person can compare. `null` while the read
               is in flight or if the version is gone, which `factsOf` renders as the id rather than
               as a number nobody measured. */
            versionNumber:
              versions.data?.items.find(
                (version) => version.id === found.dataset_version_id,
              )?.version_number ?? null,
            /* The run itself rather than the listing: "which execution found this" has an
               answer one click away, and the id was previously a value to paste into a URL. */
            executionHref: `${SCREEN_PATHS.executions}/${found.execution_id}`,
          }).map((fact) => (
            <div key={fact.key} className="lq-rm__fact">
              <div className="lq-rm__factKey">{t(fact.key)}</div>
              <FactValue fact={fact} />
            </div>
          ))}
          <p className="lq-note">{t('remediation.planNote')}</p>
        </div>

        <ArtifactPanel plan={found} />
      </div>

      <div className="lq-rm__panel">
        <div className="lq-rm__panelTitle">{t('remediation.lifecycle')}</div>
        {lifecycleOf(found).map((entry) => (
          <div key={entry.verbKey} className="lq-rm__life">
            <div className="lq-rm__lifeWhen">{asInstant(entry.when)}</div>
            <div className="lq-rm__lifeVerb">{t(entry.verbKey)}</div>
            <div className="lq-rm__lifeWho">
              {entry.who ?? (
                <span className="lq-muted">{t('remediation.byPlatform')}</span>
              )}
            </div>
          </div>
        ))}
        <p className="lq-note">{t('remediation.lifecycleNote')}</p>
      </div>

      {/* Stated rather than left as an absence: `rowsReading` is what the queue row showed, and a
          reader arriving here from an unsimulated row needs the same sentence. */}
      {rows.state === 'unsimulated' && (
        <p className="lq-note">{t('remediation.rowsNote.unsimulated')}</p>
      )}
    </div>
  );
}

/** One fact's value: a link, a translated label, or text — with the whole id on hover.
 *
 * A component rather than three branches inline, because the panel renders seven of these and the
 * distinction between "this is data" and "this is a sentence standing in for missing data" has to
 * be made identically each time. `muted` is the second: a fallback rendered in the same weight as a
 * measured value is a fallback a reader takes for one.
 */
function FactValue({ fact }: { readonly fact: Fact }): ReactElement {
  const { t } = useTranslation();
  const shown = fact.translate === true ? t(fact.value) : fact.value;
  const className = [
    'lq-rm__factValue',
    fact.mono ? 'lq-mono' : '',
    fact.muted === true ? 'lq-muted' : '',
  ]
    .filter((part) => part !== '')
    .join(' ');

  if (fact.to !== undefined) {
    return (
      <div className={className} title={fact.expands}>
        <Link to={fact.to}>{shown}</Link>
      </div>
    );
  }
  return (
    <div className={className} title={fact.expands}>
      {shown}
    </div>
  );
}

/** The plan as sentences an approver can sign, shown while their signature is being asked for.
 *
 * One line per applicable proposal — "and 3 more" is not a thing to sign — each with its measured
 * count beside it, and the two promises that frame the whole feature: nothing is invented, and
 * approving unlocks a script rather than touching data. What the plan does NOT fix is stated too,
 * because a partial fix presented without its denominator reads as a complete one.
 */
function SigningBrief({ plan }: { readonly plan: Plan }): ReactElement {
  const { t } = useTranslation();
  const lines = signingLines(plan);
  const flagged = unfixableCount(plan);

  return (
    <div className="lq-rm__brief">
      <div className="lq-rm__briefTitle">{t('remediation.does.title')}</div>
      <p className="lq-rm__briefIntro">{t('remediation.does.intro')}</p>
      <ul className="lq-rm__briefList">
        {lines.map((line) => (
          <li key={line.id}>
            <span>{t(line.key, line.args)}</span>
            <span className="lq-rm__briefRows">
              {line.rows === null
                ? t('remediation.does.unsimulated')
                : t('remediation.does.rows', { rows: line.rows })}
            </span>
          </li>
        ))}
      </ul>
      {flagged > 0 && (
        <p className="lq-rm__briefFlagged">
          {t('remediation.does.flagged', { count: flagged })}
        </p>
      )}
      <p className="lq-rm__briefPromise">{t('remediation.does.noInvention')}</p>
      <p className="lq-rm__briefPromise">{t(modeConsequenceKey(plan))}</p>
    </div>
  );
}

/** The lifecycle controls, offered only where the plan's state admits them.
 *
 * **A decision carries a reason and the field is required here because it is required there.** A
 * rejection with no stated reason is what an incident review a year later cannot work with, so the
 * two decision buttons stay closed until one is typed rather than sending an empty string the
 * server would refuse.
 *
 * **Simulate reads the connection off the plan's own dataset**, never from a picker. The connection
 * is a fact about where the dataset lives; asking a person to choose one is asking them to be right
 * about something the platform already knows, and a wrong choice would open a read-only session
 * against somebody else's database.
 */
function PlanActions({ plan }: { readonly plan: Plan }): ReactElement {
  const { t } = useTranslation();
  const act = usePlanAction();
  const dataset = useDataset(plan.dataset_id);
  const [reason, setReason] = useState('');

  const offered = offeredFor(plan);
  const decides = offered.some(
    (each) => each.action === 'approve' || each.action === 'reject',
  );
  /* What to tell this reader about signing, when there is something to say. Rendered rather than
     left as an absence: a plan that says "waiting on a second person" above a row of buttons that
     does not include Approve reads as a screen that failed to load one. */
  const notice = decisionNotice(plan);
  const needsReason = (action: string): boolean =>
    action === 'approve' || action === 'reject';

  return (
    <>
      {notice !== null && (
        <div
          className={notice.kind === 'signed' ? 'lq-rm__signed' : 'lq-rm__refused'}
          role="status"
        >
          {/* **Two headings, because they are two different facts.** A signature is a receipt —
              telling somebody who just approved that they "cannot sign this one" presents their own
              success as a rejection, which is what shipped and what this fixes. The button is
              correctly withheld in both cases; only the sentence differs. */}
          <strong>
            {notice.kind === 'signed'
              ? t('remediation.youSigned')
              : t('remediation.notYours')}
          </strong>
          {/* The server's own sentence, verbatim. Restating it here would be a second explanation
              of a rule this screen does not implement, and the two would drift. */}
          <span>{notice.detail}</span>
        </div>
      )}
      {decides && (
        <div className="lq-rm__reason">
          <TextField
            label={t('remediation.reasonLabel')}
            value={reason}
            onChange={setReason}
            hint={t('remediation.reasonHint')}
          />
        </div>
      )}
      <div className="lq-rm__actions">
        {offered.map((each) => {
          /* Simulate cannot be offered until the dataset read has answered, because the connection
             is what the request carries. Disabled with the reason rather than hidden: a control
             that appears a moment later reads as a glitch. */
          const waiting = each.action === 'simulate' && !dataset.isSuccess;
          const blocked = needsReason(each.action) && reason.trim() === '';
          return (
            <button
              key={each.action}
              type="button"
              className={each.primary ? 'lq-rm__act lq-rm__act--primary' : 'lq-rm__act'}
              disabled={act.isPending || waiting || blocked}
              title={waiting ? t('remediation.awaitingDataset') : undefined}
              onClick={() => {
                act.mutate(
                  {
                    planId: plan.id,
                    action: each.action,
                    ...(needsReason(each.action) ? { reason: reason.trim() } : {}),
                    ...(each.action === 'simulate' && dataset.isSuccess
                      ? { connectionId: dataset.data.connection_id }
                      : {}),
                  },
                  {
                    onSuccess: () => {
                      setReason('');
                    },
                  },
                );
              }}
            >
              {t(`remediation.action.${each.action}`)}
            </button>
          );
        })}
        {blockedFor(plan).map((key) => (
          <span key={key} className="lq-rm__blocked" title={t(`${key}.why`)}>
            {t(key)}
          </span>
        ))}
      </div>
      {act.isError && (
        <Alert tone="error" title={t('remediation.actionFailed')}>
          {act.error.message}
        </Alert>
      )}
      {dataset.isError && (
        /* Simulate is disabled above, and this says why rather than leaving a dead control. */
        <Alert tone="warning">{t('remediation.datasetUnreadable')}</Alert>
      )}
    </>
  );
}

function ArtifactPanel({ plan }: { readonly plan: Plan }): ReactElement {
  const { t } = useTranslation();
  /* Only asked for once a plan has emitted something. A plan that never has would answer with an
     empty list, which is honest and is still a round trip per page view for a panel whose answer
     the plan's own state already gives. */
  const artifacts = usePlanArtifacts(plan.id, hasArtifact(plan));

  return (
    <div className="lq-rm__panel">
      <div className="lq-rm__panelTitle">{t('remediation.artifact')}</div>
      {!hasArtifact(plan) && (
        <div className="lq-rm__absent">{t('remediation.artifactAbsent')}</div>
      )}
      {artifacts.isError && (
        <Alert tone="error" title={t('remediation.artifactFailed')}>
          {artifacts.error.message}
        </Alert>
      )}
      {artifacts.isSuccess && artifacts.data.length === 0 && (
        /* The state says emitted and the store holds nothing. Reported rather than rendered as an
           empty panel, because those are different facts and one of them is a problem. */
        <div className="lq-rm__absent">{t('remediation.artifactMissing')}</div>
      )}
      {artifacts.isSuccess &&
        artifacts.data.map((artifact) => (
          <div key={artifact.checksum} className="lq-rm__artifact">
            <ArtifactDownload planId={plan.id} filename={artifact.filename} />
            <div className="lq-rm__checksum lq-mono" title={artifact.checksum}>
              {artifact.checksum.slice(0, 16)}…
            </div>
            <div className="lq-cell__secondary">{asInstant(artifact.emitted_at)}</div>
          </div>
        ))}
      {artifacts.isSuccess && artifacts.data.length > 0 && (
        <p className="lq-note">{t('remediation.artifactNote')}</p>
      )}
    </div>
  );
}

/** The script's name and the control that fetches it.
 *
 * **A button rather than a link, and that is the fix.** `<a href>` navigates, and a navigation
 * carries no bearer token — so the route answered 401 and replaced the app with the API's problem
 * document. `downloadFile` fetches it as the signed-in user and hands the bytes to the browser.
 *
 * Held in its own component so the pending flag belongs to one artifact: a panel-level flag would
 * disable every row's control while one of them was in flight, and a plan can hold more than one
 * emission.
 */
function ArtifactDownload({
  planId,
  filename,
}: {
  readonly planId: string;
  readonly filename: string;
}): ReactElement {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function save(): Promise<void> {
    setSaving(true);
    setFailure(null);
    try {
      /* The server names the file in `Content-Disposition`; the listing's own name is the fallback,
         so the two cannot disagree about which artifact was handed over. */
      await downloadFile(artifactUrl(planId), filename);
    } catch (error) {
      /* The message is the server's problem detail, which distinguishes a permission refusal from a
         plan nobody has emitted. Reported rather than swallowed: a download that quietly does
         nothing is indistinguishable from one the browser is still working on. */
      setFailure(
        error instanceof Error ? error.message : t('remediation.downloadFailed'),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="lq-rm__artifactHead">
        <span className="lq-mono lq-rm__artifactName">{filename}</span>
        <Button
          size="compact"
          busy={saving}
          onClick={() => {
            void save();
          }}
        >
          {t('remediation.download')}
        </Button>
      </div>
      {failure !== null && (
        <Alert tone="error" title={t('remediation.downloadFailed')}>
          {failure}
        </Alert>
      )}
    </>
  );
}

/** The script this plan would emit, rendered on request and never on mount.
 *
 * **This closes an ordering problem rather than adding a convenience.** Emission requires two
 * signatures, so until this existed the two people signing for a change to a customer's production
 * data could not read the SQL until after they had signed for it -- `SigningBrief` above was built
 * as a substitute and says exactly that in its own comment. The brief stays: it is the plan as
 * sentences, and this is the plan as statements, and an approver wants both.
 *
 * **Behind a control, which is the same decision the assessment makes one section up.** That one is
 * gated because it dials a customer's database; this one is gated because handing over a script
 * should be something somebody asked for -- the sibling `/artifacts` route withholds script content
 * for that exact reason, so fetching it on every page render would defeat the rule one route over.
 * The empty state reuses the assessment's own shape, so a reader meets no new affordance.
 *
 * **No download and no copy button, deliberately.** The user's flow is read, approve, then take the
 * file -- and the file is what carries the checksum somebody signed. A copy control here would be a
 * quiet way around the approval this section exists to inform, on text whose own header says not to
 * run it.
 */
function ScriptSection({ plan }: { readonly plan: Plan }): ReactElement {
  const { t } = useTranslation();
  /* Local state rather than a URL parameter: whether somebody has opened the script is not a fact
     about the plan, and putting it in the address would make a shared link fetch a script for
     whoever opened it. */
  const [asked, setAsked] = useState(false);
  const preview = useScriptPreview(plan.id, asked);

  if (!asked) {
    return (
      <div className="lq-cp__none">
        <div className="lq-cp__noneTitle">{t('remediation.script.absentTitle')}</div>
        <div className="lq-cp__noneBody">{t('remediation.script.absentBody')}</div>
        <div className="lq-rm__assessAction">
          <Button
            tone="secondary"
            onClick={() => {
              setAsked(true);
            }}
          >
            {t('remediation.script.show')}
          </Button>
        </div>
      </div>
    );
  }

  /* A failed render is reported as a failure of the request, never as an absence of a script. The
     server's own `detail` carries the reason -- an advise-mode plan emits nothing to run, an
     abandoned plan can never be approved -- and restating those here would be a second place
     deciding what a refusal means. */
  if (preview.isError) {
    return (
      <Alert tone="error" title={t('remediation.script.failed')}>
        {preview.error.message}
      </Alert>
    );
  }
  if (!preview.isSuccess) {
    return <p className="lq-note">{t('remediation.script.loading')}</p>;
  }

  const rendered = preview.data;

  return (
    <>
      {/* `Alert` rather than a hand-rolled band, which is what the assessment refusal above uses
          for the same shape -- a verdict and then its explanation. The first draft wrote a bare
          band and left the title copy unrendered, which a test caught: a copy key nothing renders
          is the dead-mechanism shape, and here the unrendered half was the sentence that matters
          most.

          **Keyed on `approved` from the payload, never on the plan's state.** `emitted` and
          `executed` are both past approval, so a check against one state name would call them
          unapproved and stamp "do not run this" on a script somebody had already signed. That flag
          is derived from the same `decided_by` the script's own header is rendered from, so the
          alert and the text below it cannot disagree.

          `warning`, not `error`: an unapproved rendering is the ordinary state of this route and
          nothing has gone wrong. `error` here would make the section look broken every time it
          worked.

          The title is **spread rather than passed as `undefined`**, because
          `exactOptionalPropertyTypes` is on and the two are not the same thing -- the
          `AuditFilters` lesson, met again. */}
      <Alert
        tone={rendered.approved ? 'notice' : 'warning'}
        {...(rendered.approved ? {} : { title: t('remediation.script.unapprovedTitle') })}
      >
        {rendered.approved
          ? t('remediation.script.approvedBody')
          : t('remediation.script.unapprovedBody')}
      </Alert>

      <div className="lq-rm__script">
        <div className="lq-rm__scriptHead">
          <span className="lq-mono lq-rm__artifactName">{rendered.filename}</span>
          <span className="lq-rm__scriptName">
            {t('remediation.script.willEmit', { filename: rendered.filename })}
          </span>
        </div>
        {/* `<pre>` with the content as a child, so nothing interprets it. The script is host-written
            SQL and React escapes it either way, and a `<code>` inside would add a second element
            with no styling of its own. */}
        <pre className="lq-mono lq-rm__scriptBody">{rendered.content}</pre>
      </div>

      <p className="lq-note">{t('remediation.script.note')}</p>
    </>
  );
}
